import { ApiQueryName } from "@/models/api"
import { Site } from "@/models/site"
import type { PaginatedData } from "@/nzds/data-grid"
import {
  type SpraypaintInfiniteRecord,
  useSpraypaintInfiniteQuery,
} from "@/services/utils/spraypaint"
import type { HttpError } from "@/utils/api"
import { toHttpError } from "@/utils/api"
import type { UseInfiniteQueryOptions } from "@tanstack/react-query"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

import type { SpraypaintQueryOptions } from "../types"
import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"

type SiteWithRequiredId = SpraypaintInfiniteRecord<Site>
interface PageCountStats {
  number: number
  pages: number
  size: number
  total: number
}

interface CollectionMetaWithPageCount {
  stats?: {
    page?: {
      count?: PageCountStats
    }
  }
}

interface UseSitesInfiniteOptions {
  apiQueryOptions?: SpraypaintQueryOptions
  initialPage?: number
  queryOptions?: Omit<
    UseInfiniteQueryOptions<PaginatedData<SiteWithRequiredId>, HttpError>,
    | "queryKey"
    | "queryFn"
    | "getNextPageParam"
    | "getPreviousPageParam"
    | "initialPageParam"
    | "select"
  >
}

const defaultPageSize = 250

const getSites = async (
  options: SpraypaintQueryOptions
): Promise<PaginatedData<SiteWithRequiredId>> => {
  const { extraFields, includes, page, pageSize, sort, where } = options
  const requestedPage = page ?? 1
  const requestedPageSize = pageSize ?? defaultPageSize

  try {
    const response: CollectionProxy<Site> = await Site.where(where ?? {})
      .includes(includes ?? [])
      .order(sort ?? { name: "asc" })
      .page(requestedPage)
      .per(requestedPageSize)
      .selectExtra(extraFields ?? [])
      .all()

    const items = response.data.filter(
      (site): site is SiteWithRequiredId =>
        site.id !== undefined && site.id !== null
    )

    const stats = (response.meta as CollectionMetaWithPageCount | undefined)
      ?.stats?.page?.count

    return {
      items,
      page: stats?.number ?? requestedPage,
      pages:
        stats?.pages ??
        (items.length < requestedPageSize ? requestedPage : requestedPage + 1),
      size: stats?.size ?? requestedPageSize,
      total:
        stats?.total ?? items.length + (requestedPage - 1) * requestedPageSize,
    }
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const useSitesInfinite = ({
  apiQueryOptions = {},
  initialPage,
  queryOptions = {},
}: UseSitesInfiniteOptions = {}) => {
  const queryKey = createQueryKey(ApiQueryName.Sites, "getMany", null, {
    apiQueryOptions,
  })

  const queryResult = useSpraypaintInfiniteQuery<SiteWithRequiredId>({
    apiQueryOptions,
    defaultPageSize,
    fetchPage: getSites,
    initialPage,
    queryKey,
    queryOptions,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Sites)
}
